/**
 * ASC 606 Phase 11g — Cost Ledger Panel (11b/11c Cost-to-Cost surface).
 *
 * Per-POB view:
 *   - Cost baseline + actual-to-date summary (11b)
 *   - Cost ledger entries (append-only, includes 25-36 carve-outs)
 *   - "Set Baseline" modal (POST /cost-baseline)
 *   - "Add Cost Entry" modal (POST /cost-entries)
 *   - "Run Recognition" button (POST /recognize-cost-to-cost)
 *   - Inline RecognitionEventsPanel filtered to COST_TO_COST
 *
 * Backend endpoints:
 *   GET /contracts/{id}/cost-summary
 *   GET /contracts/{id}/cost-entries?pob_id=...
 *   POST /contracts/{id}/pobs/{pob_id}/cost-baseline
 *   POST /contracts/{id}/cost-entries
 *   POST /contracts/{id}/pobs/{pob_id}/recognize-cost-to-cost
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';
import DataTable, { Column } from './DataTable';
import Modal from './Modal';
import RecognitionEventsPanel from './RecognitionEventsPanel';

const API = `${API_V1}/revenue`;

interface CostSummaryPOB {
  pob_id: number;
  po_number: number;
  description: string;
  progress_method: string | null;
  estimated_total_cost: number | null;
  actual_cost_raw: number;
  actual_cost_excluding_carve_outs: number;
  pct_complete_informational: number | null;
  baseline_set_at: string | null;
}

interface CostLedgerEntry {
  id: number;
  revenue_contract_id: number;
  pob_id: number | null;
  source_type: string;
  source_id: number | null;
  source_reference: string | null;
  cost_amount: number;
  currency: string;
  incurred_date: string | null;
  period: string;
  excluded_from_progress: boolean;
  exclusion_reason: string | null;
  notes: string | null;
  entered_by: number | null;
  created_at: string | null;
}

interface Props {
  contractId: number;
  pobId: number;
  contractStatus: string;
  onStateChanged?: () => void;
}

export default function CostLedgerPanel({ contractId, pobId, contractStatus, onStateChanged }: Props) {
  const [summary, setSummary] = useState<CostSummaryPOB | null>(null);
  const [entries, setEntries] = useState<CostLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [baselineOpen, setBaselineOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [recogBusy, setRecogBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sumRes, entRes] = await Promise.all([
        authFetch(`${API}/contracts/${contractId}/cost-summary`),
        authFetch(`${API}/contracts/${contractId}/cost-entries?pob_id=${pobId}&limit=500`),
      ]);
      if (!sumRes.ok) throw new Error(`cost-summary HTTP ${sumRes.status}`);
      if (!entRes.ok) throw new Error(`cost-entries HTTP ${entRes.status}`);
      const sumData = await sumRes.json();
      const entData = await entRes.json();
      const found = (sumData.pobs || []).find((p: CostSummaryPOB) => p.pob_id === pobId) || null;
      setSummary(found);
      setEntries(entData.entries || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId, pobId]);

  useEffect(() => {
    load();
  }, [load]);

  const runRecognition = async () => {
    setError(null);
    setFlash(null);
    setRecogBusy(true);
    try {
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/recognize-cost-to-cost`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      if (res.ok) {
        const data = await res.json();
        setFlash(`Recognition fired: ${data.status}${data.period_delta ? ` — Δ ${formatAmount(data.period_delta)}` : ''}`);
        await load();
        if (onStateChanged) onStateChanged();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof err.detail === 'string' ? err.detail : err.detail?.message || JSON.stringify(err.detail);
        setError(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setRecogBusy(false);
    }
  };

  const entryCols: Column[] = [
    { key: 'period', label: 'Period', width: 90 },
    { key: 'incurred_date', label: 'Incurred' },
    { key: 'source_type', label: 'Source', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    { key: 'source_reference', label: 'Ref', render: (v: string | null) => v || '-' },
    {
      key: 'cost_amount',
      label: 'Amount',
      align: 'right',
      render: (v: number, row: CostLedgerEntry) => (
        <span style={{ color: row.excluded_from_progress ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
          {formatAmount(v)}
          {row.excluded_from_progress && (
            <span
              title={row.exclusion_reason || ''}
              style={{
                marginLeft: 6,
                padding: '1px 6px',
                fontSize: 9,
                fontWeight: 600,
                background: 'rgba(234,179,8,0.15)',
                color: 'var(--warning)',
                borderRadius: 3,
              }}
            >
              25-36 EXCLUDED
            </span>
          )}
        </span>
      ),
    },
    { key: 'notes', label: 'Notes', render: (v: string | null) => v || '-' },
  ];

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text-hint)' }}>Loading cost data...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          padding: 14,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 6,
        }}
      >
        <SummaryCell
          label="Baseline (25-35)"
          value={summary?.estimated_total_cost == null ? '-' : formatAmount(summary.estimated_total_cost)}
        />
        <SummaryCell
          label="Actual (Net of Carve-outs)"
          value={formatAmount(summary?.actual_cost_excluding_carve_outs || 0)}
        />
        <SummaryCell
          label="Actual (Raw)"
          value={formatAmount(summary?.actual_cost_raw || 0)}
        />
        <SummaryCell
          label="Informational Pct"
          value={summary?.pct_complete_informational == null ? '-' : `${summary.pct_complete_informational.toFixed(2)}%`}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <ActionBtn onClick={() => setBaselineOpen(true)} label="Set Baseline" disabled={(contractStatus || '').toUpperCase() === 'ACTIVE'} />
        <ActionBtn onClick={() => setEntryOpen(true)} label="Add Cost Entry" />
        <ActionBtn
          onClick={runRecognition}
          label={recogBusy ? 'Running...' : 'Run Recognition'}
          disabled={recogBusy || !summary?.estimated_total_cost}
        />
      </div>

      {error && (
        <div
          style={{
            padding: 10,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid var(--negative)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--negative)',
          }}
        >
          {error}
        </div>
      )}
      {flash && (
        <div
          style={{
            padding: 10,
            background: 'rgba(34,197,94,0.1)',
            border: '1px solid var(--positive)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--positive)',
          }}
        >
          {flash}
        </div>
      )}

      {/* Ledger */}
      <div>
        <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
          Cost Ledger (append-only)
        </h3>
        <DataTable
          columns={entryCols}
          rows={entries}
          emptyMessage="No cost entries recorded yet."
          rowKey="id"
        />
      </div>

      {/* Recognition audit */}
      <RecognitionEventsPanel
        contractId={contractId}
        pobId={pobId}
        filterMethod="COST_TO_COST"
        title="Recognition Events (Cost-to-Cost)"
      />

      {/* Modals */}
      <SetBaselineModal
        open={baselineOpen}
        onClose={() => setBaselineOpen(false)}
        contractId={contractId}
        pobId={pobId}
        onSaved={async () => {
          setBaselineOpen(false);
          setFlash('Baseline saved.');
          await load();
          if (onStateChanged) onStateChanged();
        }}
      />
      <AddEntryModal
        open={entryOpen}
        onClose={() => setEntryOpen(false)}
        contractId={contractId}
        pobId={pobId}
        onSaved={async () => {
          setEntryOpen(false);
          setFlash('Cost entry added.');
          await load();
          if (onStateChanged) onStateChanged();
        }}
      />
    </div>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)' }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginTop: 4 }}>{value}</div>
    </div>
  );
}

function ActionBtn({ onClick, label, disabled }: { onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 14px',
        fontSize: 12,
        fontWeight: 600,
        background: disabled ? 'var(--bg-hover)' : 'var(--accent)',
        color: disabled ? 'var(--text-hint)' : 'white',
        border: 'none',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// ─── Set Baseline Modal ────────────────────────────────────────

interface ModalProps {
  open: boolean;
  onClose: () => void;
  contractId: number;
  pobId: number;
  onSaved: () => void;
}

function SetBaselineModal({ open, onClose, contractId, pobId, onSaved }: ModalProps) {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    const amt = parseFloat(amount);
    if (!(amt > 0)) {
      setErr('Amount must be > 0.');
      return;
    }
    if (notes.trim().length < 30) {
      setErr('Notes must be at least 30 chars (SOX 404 audit trail).');
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/cost-baseline`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ estimated_total_cost: amt, notes: notes.trim() }),
        },
      );
      if (res.ok) {
        onSaved();
        setAmount('');
        setNotes('');
      } else {
        const body = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message || JSON.stringify(body.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Set Cost Baseline"
      description="ASC 606-10-25-35 — denominator for cost-to-cost percent-complete."
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={submit} style={btnPrimary} disabled={busy}>
            {busy ? 'Saving...' : 'Save Baseline'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FormRow label="Estimated Total Cost (USD)">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={inputStyle}
            placeholder="e.g. 150000"
            min="0"
            step="0.01"
          />
        </FormRow>
        <FormRow label="Rationale (≥30 chars, SOX 404)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            placeholder="Baseline basis — quotes, historical actuals, subcontractor bids, engineering estimates..."
          />
          <div style={{ fontSize: 10, color: notes.length >= 30 ? 'var(--positive)' : 'var(--text-hint)', marginTop: 2 }}>
            {notes.length}/30
          </div>
        </FormRow>
        {err && <InlineError>{err}</InlineError>}
      </div>
    </Modal>
  );
}

// ─── Add Cost Entry Modal ──────────────────────────────────────

function AddEntryModal({ open, onClose, contractId, pobId, onSaved }: ModalProps) {
  const [sourceType, setSourceType] = useState('MANUAL');
  const [costAmount, setCostAmount] = useState('');
  const [incurredDate, setIncurredDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [sourceRef, setSourceRef] = useState('');
  const [excluded, setExcluded] = useState(false);
  const [exclusionReason, setExclusionReason] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    const amt = parseFloat(costAmount);
    if (!amt) {
      setErr('Cost amount must be non-zero.');
      return;
    }
    if (excluded && !exclusionReason.trim()) {
      setErr('Exclusion reason required when marking excluded (25-36 carve-out).');
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/cost-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pob_id: pobId,
          source_type: sourceType,
          cost_amount: amt,
          incurred_date: incurredDate,
          period,
          source_reference: sourceRef || null,
          excluded_from_progress: excluded,
          exclusion_reason: excluded ? exclusionReason.trim() : null,
          notes: notes.trim() || null,
        }),
      });
      if (res.ok) {
        onSaved();
        setCostAmount('');
        setSourceRef('');
        setNotes('');
        setExcluded(false);
        setExclusionReason('');
      } else {
        const body = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message || JSON.stringify(body.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Cost Ledger Entry"
      description="Append-only. Idempotent via (source_type, source_id) for non-MANUAL sources."
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={submit} style={btnPrimary} disabled={busy}>
            {busy ? 'Saving...' : 'Add Entry'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FormRow label="Source Type">
          <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} style={inputStyle}>
            <option value="MANUAL">MANUAL</option>
            <option value="PROJECT_COST">PROJECT_COST</option>
            <option value="TIME_ENTRY">TIME_ENTRY</option>
            <option value="INVENTORY_TXN">INVENTORY_TXN</option>
            <option value="OTHER">OTHER</option>
          </select>
        </FormRow>
        <FormRow label="Cost Amount (signed)">
          <input
            type="number"
            value={costAmount}
            onChange={(e) => setCostAmount(e.target.value)}
            style={inputStyle}
            step="0.01"
          />
        </FormRow>
        <FormRow label="Incurred Date">
          <input type="date" value={incurredDate} onChange={(e) => setIncurredDate(e.target.value)} style={inputStyle} />
        </FormRow>
        <FormRow label="Period (YYYY-MM)">
          <input type="text" value={period} onChange={(e) => setPeriod(e.target.value)} style={inputStyle} maxLength={7} />
        </FormRow>
        <FormRow label="Source Reference (optional)">
          <input type="text" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} style={inputStyle} />
        </FormRow>
        <FormRow label="25-36 Carve-out?">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
            <input type="checkbox" checked={excluded} onChange={(e) => setExcluded(e.target.checked)} />
            <span style={{ fontSize: 12 }}>Exclude from progress</span>
          </label>
        </FormRow>
        {excluded && (
          <div style={{ gridColumn: '1 / 3' }}>
            <FormRow label="Exclusion Reason (required)">
              <textarea
                value={exclusionReason}
                onChange={(e) => setExclusionReason(e.target.value)}
                style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }}
                placeholder="e.g. uninstalled materials, wastage batch #..."
              />
            </FormRow>
          </div>
        )}
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }}
            />
          </FormRow>
        </div>
      </div>
      {err && <InlineError>{err}</InlineError>}
    </Modal>
  );
}

// ─── Shared form primitives ────────────────────────────────────

export function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</label>
      {children}
    </div>
  );
}

export function InlineError({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: 8,
        background: 'rgba(239,68,68,0.1)',
        border: '1px solid var(--negative)',
        borderRadius: 4,
        fontSize: 12,
        color: 'var(--negative)',
      }}
    >
      {children}
    </div>
  );
}

export const inputStyle: React.CSSProperties = {
  padding: 6,
  fontSize: 12,
  background: 'var(--bg-card)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 4,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  width: '100%',
};

export const btnPrimary: React.CSSProperties = {
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 600,
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

export const btnSecondary: React.CSSProperties = {
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 600,
  background: 'var(--bg-hover)',
  color: 'var(--text-primary)',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};
